import { useState, useEffect } from "react";
import { useTonWallet, useTonConnectUI } from "@tonconnect/ui-react";
import { rpcCall, subscribe } from "../ws/client";
import { toast } from "./ui/Toast";
import { handleTransactionError, checkAndNotifyPending } from "../utils/txRecovery";
import { Button } from "./ui";
import "../styles/Lottery.scss";
import { loadBalanceFromCache, saveBalanceToCache, isRateLimitError } from "../utils/balanceCache";
import { useNetwork } from "../hooks/useNetwork";

const TICKET_PRICE = 5000; // ODDB
const ODDB_ADDRESS = "0:97b5ba103e3c189cc82cfa4ddc68b9b87a55cb6eb1cee49bf424e295eb5ec73b";

interface Lottery {
    _id: string;
    status: string;
    prize_ton: number;
    prize_per_winner: number;
    winners_count: number;
    created_at: string;
    completed_at?: string;
    winner_tickets: string[];
    total_tickets: number;
}

interface Ticket {
    _id: string;
    lottery_id: string;
    wallet_address: string;
    ticket_number: string;
    is_winner: boolean;
    prize_amount: number;
    purchased_at: string;
    lottery_status?: string;
    lottery_prize?: number;
}

interface LotteryResults {
    lottery: Lottery;
    total_tickets: number;
    winners: Ticket[];
    losers: Ticket[];
}

export default function Lottery() {
    const wallet = useTonWallet();
    const { network } = useNetwork();
    const [tonConnectUI] = useTonConnectUI();
    const [loading, setLoading] = useState(true);
    const [activeLottery, setActiveLottery] = useState<Lottery | null>(null);
    const [myTickets, setMyTickets] = useState<Ticket[]>([]);
    const [allLotteries, setAllLotteries] = useState<Lottery[]>([]);
    const [selectedLotteryResults, setSelectedLotteryResults] = useState<LotteryResults | null>(null);
    const [buying, setBuying] = useState(false);
    const [oddbBalance, setOddbBalance] = useState<number>(0);

    useEffect(() => {
        loadData();
        
        // Subscribe to lottery updates
        const unsubscribe = subscribe("lottery.updated", () => {
            loadData();
        });
        
        return () => unsubscribe();
    }, [wallet]);

    async function loadData() {
        try {
            // Load active lottery (available for all users)
            const activeResult = await rpcCall("lottery.get_active", {}) as any;
            console.log("[Lottery] Active lottery result:", activeResult);
            console.log("[Lottery] Active lottery data:", activeResult.result?.lottery);
            setActiveLottery(activeResult.result?.lottery);

            // Load all lotteries
            const lotteriesResult = await rpcCall("lottery.list", { limit: 5 }) as any;
            setAllLotteries(lotteriesResult.result?.lotteries || []);

            // Load user-specific data only if wallet is connected
            if (wallet?.account?.address) {
                // Load user's tickets
                const ticketsResult = await rpcCall("lottery.my_tickets", {
                    wallet: wallet.account.address,
                }) as any;
                setMyTickets(ticketsResult.result?.tickets || []);

                // Check ODDB balance
                const balanceResult = await rpcCall("wallet.check_balance", {
                    wallet: wallet.account.address,
                    jetton_address: ODDB_ADDRESS,
                    amount: TICKET_PRICE,
                }) as any;
                
                if (balanceResult.error) {
                    // Check if it's a rate limit error
                    if (isRateLimitError(balanceResult.error.message)) {
                        console.warn("⚠️ [Lottery] Rate limit error, trying cache...");
                        const cached = loadBalanceFromCache(
                            wallet.account.address,
                            ODDB_ADDRESS,
                            TICKET_PRICE,
                            network
                        );
                        
                        if (cached) {
                            console.log("✅ [Lottery] Using cached balance data");
                            setOddbBalance(cached.jetton_balance || 0);
                        } else {
                            console.warn("[Lottery] No cached balance available");
                            setOddbBalance(0);
                        }
                    } else {
                        console.error("[Lottery] Balance check error:", balanceResult.error.message);
                        setOddbBalance(0);
                    }
                } else if (balanceResult.result) {
                    // Save to cache
                    saveBalanceToCache(
                        wallet.account.address,
                        ODDB_ADDRESS,
                        TICKET_PRICE,
                        network,
                        {
                            can_sell: balanceResult.result.can_sell,
                            ton_balance: balanceResult.result.ton_balance,
                            jetton_balance: balanceResult.result.jetton_balance,
                            min_ton_required: balanceResult.result.min_ton_required || 0,
                            jetton_required: TICKET_PRICE,
                            errors: balanceResult.result.errors
                        }
                    );
                    setOddbBalance(balanceResult.result?.jetton_balance || 0);
                }
            } else {
                setMyTickets([]);
                setOddbBalance(0);
            }
        } catch (err) {
            console.error("Error loading lottery data:", err);
        } finally {
            setLoading(false);
        }
    }

    async function handleBuyTicket() {
        if (!wallet?.account?.address) {
            toast.error("Пожалуйста, подключите кошелек");
            return;
        }

        if (!activeLottery) {
            toast.error("Нет активной лотереи");
            return;
        }

        if (oddbBalance < TICKET_PRICE) {
            toast.error(`Недостаточно ODDB. Требуется: ${TICKET_PRICE} ODDB`);
            return;
        }

        try {
            setBuying(true);
            
            // Step 1: Prepare transaction
            console.log("[Lottery] Preparing ticket purchase...");
            const prepareResult = await rpcCall("lottery.prepare_purchase", {
                wallet: wallet.account.address,
            }) as any;

            const prepareData = prepareResult.result || prepareResult;
            
            if (!prepareData.success) {
                toast.error(typeof prepareData.error === 'string' ? prepareData.error : "Не удалось подготовить транзакцию");
                return;
            }

            const pendingTxId = prepareData.pending_tx_id;
            console.log("[Lottery] Transaction prepared:", prepareData.transaction);
            console.log("[Lottery] Pending TX ID:", pendingTxId);
            
            // Step 2: Send transaction through Tonkeeper
            toast.info("Подтвердите транзакцию в кошельке...");
            
            let txResult;
            try {
                txResult = await tonConnectUI.sendTransaction({
                    validUntil: prepareData.transaction.validUntil,
                    messages: prepareData.transaction.messages
                });
                console.log("[Lottery] Transaction sent:", txResult);
            } catch (sendError: any) {
                // Handle SDK errors with recovery
                await handleTransactionError(sendError, pendingTxId, loadData);
                return;
            }
            
            // Step 3: Confirm purchase on backend
            const confirmResult = await rpcCall("lottery.confirm_purchase", {
                wallet: wallet.account.address,
                lottery_id: prepareData.lottery_id,
                tx_hash: txResult.boc,
                pending_tx_id: pendingTxId
            }) as any;

            const confirmData = confirmResult.result || confirmResult;
            
            if (confirmData.success) {
                toast.success(`Билет куплен! Номер: ${confirmData.ticket.ticket_number.slice(0, 8)}...`);
                await loadData();
            } else {
                toast.error(typeof confirmData.error === 'string' ? confirmData.error : "Не удалось подтвердить покупку");
            }
        } catch (err: any) {
            console.error("[Lottery] Purchase error:", err);
            
            if (err?.message?.includes("user rejected")) {
                toast.error("Транзакция отменена");
            } else {
                toast.error(err.message || "Не удалось купить билет");
            }
        } finally {
            setBuying(false);
        }
    }

    async function loadLotteryResults(lotteryId: string) {
        try {
            const result = await rpcCall("lottery.get_results", {
                lottery_id: lotteryId,
            }) as any;
            setSelectedLotteryResults(result.result?.results);
        } catch (err: any) {
            toast.error(err.message || "Не удалось загрузить результаты");
        }
    }

    const myActiveTickets = myTickets.filter(t => t.lottery_status === "active");
    const myCompletedTickets = myTickets.filter(t => t.lottery_status === "completed");

    if (loading) {
        return (
            <div className="lottery-container">
                <div className="loading">Загрузка лотереи...</div>
            </div>
        );
    }

    return (
        <div className="lottery-container">
            <div className="lottery-header">
                <h1>🎟 Лотерея</h1>
                <p className="lottery-description">
                    Покупайте билеты за ODDB токены и выигрывайте TON! В каждой лотерее 5 победителей.
                </p>
            </div>

            {/* Active Lottery */}
            {activeLottery ? (
                <div className="lottery-card active-lottery">
                    <div className="lottery-status">
                        <span className="status-badge active">АКТИВНА</span>
                    </div>
                    
                    <div className="lottery-info">
                        <div className="info-row">
                            <div className="info-item">
                                <div className="info-label">Призовой фонд</div>
                                <div className="info-value prize">{activeLottery.prize_ton.toFixed(2)} TON</div>
                            </div>
                            <div className="info-item">
                                <div className="info-label">На победителя</div>
                                <div className="info-value">{activeLottery.prize_per_winner.toFixed(4)} TON</div>
                            </div>
                        </div>
                        
                        <div className="info-row">
                            <div className="info-item">
                                <div className="info-label">Победителей</div>
                                <div className="info-value">{activeLottery.winners_count}</div>
                            </div>
                            <div className="info-item">
                                <div className="info-label">Билетов продано</div>
                                <div className="info-value">{activeLottery.total_tickets}</div>
                            </div>
                        </div>
                    </div>

                    <div className="ticket-purchase">
                        <div className="purchase-info">
                            <div className="ticket-price">
                                <span className="price-label">Цена билета:</span>
                                <span className="price-value">{TICKET_PRICE.toLocaleString()} ODDB</span>
                            </div>
                            <div className="balance-info">
                                <span>Ваш баланс: {oddbBalance.toLocaleString()} ODDB</span>
                            </div>
                        </div>
                        
                        <Button
                            onClick={handleBuyTicket}
                            disabled={buying || oddbBalance < TICKET_PRICE}
                            className="buy-ticket-btn"
                        >
                            {buying ? "Покупка..." : "Купить билет"}
                        </Button>
                    </div>

                    {myActiveTickets.length > 0 && (
                        <div className="my-active-tickets">
                            <h3>Ваши билеты ({myActiveTickets.length})</h3>
                            <div className="tickets-list">
                                {myActiveTickets.map((ticket) => (
                                    <div key={ticket._id} className="ticket-item">
                                        <span className="ticket-icon">🎟</span>
                                        <span className="ticket-number">#{ticket.ticket_number.slice(0, 8)}...</span>
                                        <span className="ticket-date">
                                            {new Date(ticket.purchased_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="lottery-card no-lottery">
                    <p>В данный момент активных лотерей нет. Загляните позже!</p>
                </div>
            )}

            {/* My Completed Tickets */}
            {myCompletedTickets.length > 0 && (
                <div className="lottery-card my-tickets">
                    <h2>Завершенные билеты</h2>
                    <div className="tickets-list">
                        {myCompletedTickets.map((ticket) => (
                            <div 
                                key={ticket._id} 
                                className={`ticket-item ${ticket.is_winner ? 'winner' : 'loser'}`}
                            >
                                <div className="ticket-info">
                                    <span className="ticket-number">
                                        {ticket.is_winner ? '🏆' : '🎟'} #{ticket.ticket_number.slice(0, 8)}...
                                    </span>
                                    <span className="ticket-date">
                                        {new Date(ticket.purchased_at).toLocaleDateString()}
                                    </span>
                                </div>
                                <div className="ticket-result">
                                    {ticket.is_winner ? (
                                        <span className="winner-badge">
                                            Выигрыш! +{ticket.prize_amount.toFixed(4)} TON
                                        </span>
                                    ) : (
                                        <span className="loser-badge">Не выиграл</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Lottery History */}
            {allLotteries.length > 0 && (
                <div className="lottery-card lottery-history">
                    <h2>История лотерей</h2>
                    <div className="history-list">
                        {allLotteries.map((lottery) => (
                            <div key={lottery._id} className="history-item">
                                <div className="history-info">
                                    <div className="history-header">
                                        <span className={`status-badge ${lottery.status}`}>
                                            {lottery.status === 'active' ? 'АКТИВНА' : lottery.status === 'completed' ? 'ЗАВЕРШЕНА' : lottery.status.toUpperCase()}
                                        </span>
                                        <span className="history-date">
                                            {new Date(lottery.created_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                    <div className="history-stats">
                                        <span>Приз: {lottery.prize_ton.toFixed(2)} TON</span>
                                        <span>•</span>
                                        <span>{lottery.total_tickets} билетов</span>
                                    </div>
                                </div>
                                {lottery.status === 'completed' && (
                                    <Button
                                        onClick={() => loadLotteryResults(lottery._id)}
                                    >
                                        Посмотреть результаты
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Results Modal */}
            {selectedLotteryResults && (
                <div className="modal-overlay" onClick={() => setSelectedLotteryResults(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Результаты лотереи</h2>
                            <button 
                                className="close-btn" 
                                onClick={() => setSelectedLotteryResults(null)}
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div className="results-summary">
                            <div className="summary-item">
                                <span className="summary-label">Всего билетов</span>
                                <span className="summary-value">{selectedLotteryResults.total_tickets}</span>
                            </div>
                            <div className="summary-item">
                                <span className="summary-label">Призовой фонд</span>
                                <span className="summary-value">
                                    {selectedLotteryResults.lottery.prize_ton.toFixed(2)} TON
                                </span>
                            </div>
                        </div>

                        <div className="results-section">
                            <h3>🏆 Победители ({selectedLotteryResults.winners.length})</h3>
                            <div className="winners-list">
                                {selectedLotteryResults.winners.map((ticket) => (
                                    <div key={ticket._id} className="winner-item">
                                        <span className="winner-ticket">
                                            #{ticket.ticket_number.slice(0, 8)}...
                                        </span>
                                        <span className="winner-prize">
                                            +{ticket.prize_amount.toFixed(4)} TON
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
